import { useMemo } from 'react'

import {
  useCubeReportAccessFiltersContext,
  useCubeReportPageFiltersContext,
} from 'cube'
import { CONVERSATION_FACTS_DIMENSIONS } from 'cube/constants/QueryConstants'
import { ConversationCountQuery } from 'cube/queries'

import { CubeQueryBuilder, useCubeQuery } from '@driftt/dds.reports'

import { resolveCubeKPIResults } from 'components/KPICard'
import useTimePeriod from 'hooks/useTimePeriod'

export interface IConversationCount {
  metric?: number | null
  previousMetric?: number | null
  isLoading: boolean
  hasNoData: boolean
  hasError: boolean
}

/***
 * Hook that uses cube to fetch conversation count data for the current and previous time period.
 *
 * @returns {IConversationCount}
 */
export const useConversationCount = (): IConversationCount => {
  const timePeriod = useTimePeriod()
  const { pageFilters } = useCubeReportPageFiltersContext()
  const { accessFilters } = useCubeReportAccessFiltersContext()

  const cubeQueryCurrent = new CubeQueryBuilder({
    query: ConversationCountQuery,
  })
    .withName('ConversationCountCurrent')
    .withFilters([...pageFilters, ...accessFilters])
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      timePeriod.startDate.toDate(),
      timePeriod.endDate.toDate()
    )
    .build()

  const cubeQueryPrevious = new CubeQueryBuilder({
    query: ConversationCountQuery,
  })
    .withName('ConversationCountPrevious')
    .withinDateRange(
      CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
      timePeriod.prevStart.toDate(),
      timePeriod.prevEnd.toDate()
    )
    .withFilters([...pageFilters, ...accessFilters])
    .build()

  const {
    data: resultSetCurrent,
    isLoading: isLoadingCurrent,
    isError: isErrorCurrent,
  } = useCubeQuery({
    query: cubeQueryCurrent,
  })

  const { data: resultSetPrevious, isLoading: isLoadingPrevious } =
    useCubeQuery({
      query: cubeQueryPrevious,
    })

  const conversationCountCurrent = useMemo(
    () =>
      resolveCubeKPIResults(
        resultSetCurrent,
        ConversationCountQuery.queryName ?? ''
      ),
    [resultSetCurrent]
  )

  const conversationCountPrevious = useMemo(
    () =>
      resolveCubeKPIResults(
        resultSetPrevious,
        ConversationCountQuery.queryName ?? ''
      ),
    [resultSetPrevious]
  )

  const metric = useMemo(
    () => conversationCountCurrent ?? null,
    [conversationCountCurrent]
  )

  const previousMetric = useMemo(
    () => conversationCountPrevious ?? null,
    [conversationCountPrevious]
  )

  const isLoading = useMemo(
    () => isLoadingCurrent || isLoadingPrevious,
    [isLoadingCurrent, isLoadingPrevious]
  )

  const hasNoData = useMemo(
    () => !isLoading && !conversationCountCurrent,
    [isLoading, conversationCountCurrent]
  )

  const hasError = useMemo(
    () => !isLoading && isErrorCurrent,
    [isLoading, isErrorCurrent]
  )

  return useMemo(
    () => ({ metric, previousMetric, isLoading, hasNoData, hasError }),
    [metric, previousMetric, isLoading, hasNoData, hasError]
  )
}

export default useConversationCount
